import React, { useEffect, useState, useMemo } from "react";
import { Plus, X, Upload, Download, Trash2, Image as ImageIcon, Film, Calendar, MapPin, Camera, Check, ChevronRight, Edit, Images } from "lucide-react";
import api, { BASE } from "../api";
import { K } from "../theme";
import { Bg, Bt, Cd, Sl, Inp } from "./index";
import { notify, confirmDialog } from "../notify";
import { fmtDMY } from "../dateFormat";
import { usePermissions } from "../usePermissions";

// Event Gallery / Media Hub — see routes/events.js for the access matrix.
// This component covers the list + filters + upload modal + detail viewer.

const EVENT_TYPES = ['Town Hall', 'Team Building', 'Festival', 'Awards', 'Training', 'CSR', 'Client Visit', 'Other'];

const fmtDate = (d) => d ? fmtDMY(d) : '—';

export const EventGallery = ({ role, myProfile, companies = [], offices = [], canDeleteEvents = false }) => {
  // WRITE (upload, edit, add more media) — HR + HR Head + admin.
  // DELETE (event or a single media item) — Settings → Permissions › Media Hub › Delete an Event
  // (Admin always).
  const { can: pcan } = usePermissions(true);
  const canWrite = pcan('hrcorner', 'media_hub', 'manage_events', 'edit');
  const canDelete = canDeleteEvents;
  const [rows, setRows] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [showUpload, setShowUpload] = useState(false);

  // Filters
  const [fCompany, setFCompany] = useState('');
  const [fOffice, setFOffice] = useState('');
  const [fType, setFType]     = useState('');
  const [fMonth, setFMonth]   = useState('');

  const load = () => {
    const p = new URLSearchParams();
    if (fCompany) p.set('company_id', fCompany);
    if (fOffice)  p.set('office_id',  fOffice);
    if (fType)    p.set('type',       fType);
    if (fMonth)   p.set('month',      fMonth);
    api.get('/api/events?' + p.toString())
      .then(d => setRows(Array.isArray(d?.rows) ? d.rows : []))
      .catch(() => setRows([]));
  };
  useEffect(load, [fCompany, fOffice, fType, fMonth]); // eslint-disable-line

  const monthOptions = useMemo(() => {
    const opts = [{ v: '', l: 'All months' }];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const v = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
      const l = d.toLocaleString('default', { month: 'long' }) + ' ' + d.getFullYear();
      opts.push({ v, l });
    }
    return opts;
  }, []);

  const galleryTotals = useMemo(() => {
    const rr = rows || [];
    const photos = rr.reduce((s, e) => s + (e.photo_count || 0), 0);
    const videos = rr.reduce((s, e) => s + (e.video_count || 0), 0);
    const mostRecent = [...rr].sort((a, b) => new Date(b.event_date) - new Date(a.event_date))[0];
    return { count: rr.length, photos, videos, mostRecent };
  }, [rows]);

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <div className="bg-s rounded-2xl border border-b0 p-4 shadow-[0_2px_10px_rgba(15,23,42,0.05)] flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] font-bold text-t3 uppercase tracking-wide">Total Events</div>
            <div className="text-[22px] font-extrabold text-t0 tracking-tight mt-[2px]">{galleryTotals.count}</div>
            <div className="text-[11px] text-t3 mt-[2px]">Logged in the gallery</div>
          </div>
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: K.pg, color: K.p }}><Camera size={20} /></div>
        </div>
        <div className="bg-s rounded-2xl border border-b0 p-4 shadow-[0_2px_10px_rgba(15,23,42,0.05)] flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] font-bold text-t3 uppercase tracking-wide">Photos</div>
            <div className="text-[22px] font-extrabold text-t0 tracking-tight mt-[2px]">{galleryTotals.photos}</div>
            <div className="text-[11px] text-t3 mt-[2px]">Across all events</div>
          </div>
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: K.blb, color: K.bl }}><Images size={20} /></div>
        </div>
        <div className="bg-s rounded-2xl border border-b0 p-4 shadow-[0_2px_10px_rgba(15,23,42,0.05)] flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] font-bold text-t3 uppercase tracking-wide">Videos</div>
            <div className="text-[22px] font-extrabold text-t0 tracking-tight mt-[2px]">{galleryTotals.videos}</div>
            <div className="text-[11px] text-t3 mt-[2px]">Across all events</div>
          </div>
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: K.pub, color: K.pu }}><Film size={20} /></div>
        </div>
        <div className="bg-s rounded-2xl border border-b0 p-4 shadow-[0_2px_10px_rgba(15,23,42,0.05)] flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] font-bold text-t3 uppercase tracking-wide">Most Recent</div>
            <div className="text-[15px] font-bold text-t0 tracking-tight mt-[2px] truncate">{galleryTotals.mostRecent?.title || '—'}</div>
            <div className="text-[11px] text-t3 mt-[2px]">{galleryTotals.mostRecent ? fmtDate(galleryTotals.mostRecent.event_date) : 'No events yet'}</div>
          </div>
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: K.gb, color: K.g }}><Calendar size={20} /></div>
        </div>
      </div>

      {/* Filter bar */}
      <Cd style={{ marginBottom: 12 }}>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[160px] flex-1">
            <div className="text-[11px] font-bold text-t3 uppercase mb-[3px]">Company</div>
            <Sl bg="bg-pb" value={fCompany} onChange={setFCompany}
                options={[{ v: '', l: 'All companies' }, ...companies.map(c => ({ v: String(c.id), l: c.name }))]} />
          </div>
          <div className="min-w-[160px] flex-1">
            <div className="text-[11px] font-bold text-t3 uppercase mb-[3px]">Location / Centre</div>
            <Sl bg="bg-pb" value={fOffice} onChange={setFOffice}
                options={[{ v: '', l: 'All locations' }, ...offices.map(o => ({ v: String(o.id), l: (o.name || o.nm) + (o.city ? ` — ${o.city}` : '') }))]} />
          </div>
          <div className="min-w-[140px]">
            <div className="text-[11px] font-bold text-t3 uppercase mb-[3px]">Event Type</div>
            <Sl bg="bg-pb" value={fType} onChange={setFType}
                options={[{ v: '', l: 'All types' }, ...EVENT_TYPES.map(t => ({ v: t, l: t }))]} />
          </div>
          <div className="min-w-[160px]">
            <div className="text-[11px] font-bold text-t3 uppercase mb-[3px]">Month / Year</div>
            <Sl bg="bg-pb" value={fMonth} onChange={setFMonth} options={monthOptions} />
          </div>
          <div className="flex-1"></div>
          {canWrite && <Bt icon={Plus} onClick={() => setShowUpload(true)}>Add Event</Bt>}
        </div>
      </Cd>

      {/* Grid */}
      {rows === null ? (
        <Cd><div className="text-center p-6 text-t3 text-[14px]">Loading…</div></Cd>
      ) : rows.length === 0 ? (
        <Cd>
          <div className="text-center p-10 text-t3 text-[14px]">
            <Camera size={22} className="mx-auto mb-2 opacity-40" />
            No events match those filters yet.
            {canWrite && <div className="mt-2 text-[14px]">Upload the first one with the button above.</div>}
          </div>
        </Cd>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map(e => <EventCard key={e.id} e={e} onOpen={() => setOpenId(e.id)} />)}
        </div>
      )}

      {showUpload && (
        <UploadEventModal
          companies={companies} offices={offices}
          myProfile={myProfile}
          onClose={() => setShowUpload(false)}
          onCreated={() => { setShowUpload(false); load(); }}
        />
      )}
      {openId && (
        <EventDetailDrawer
          id={openId} canWrite={canWrite} canDelete={canDelete}
          companies={companies} offices={offices}
          onClose={() => setOpenId(null)}
          onChanged={() => load()}
        />
      )}
    </div>
  );
};

const EventCard = ({ e, onOpen }) => {
  const cover = e.cover_photo ? BASE + '/' + e.cover_photo : null;
  return (
    <Cd style={{ padding: 0, overflow: 'hidden', cursor: 'pointer' }} onClick={onOpen}>
      <div className="aspect-[16/10] bg-bg2 flex items-center justify-center relative overflow-hidden">
        {cover
          ? <img src={cover} alt={e.title} className="w-full h-full object-cover" />
          : <ImageIcon size={30} className="text-t3 opacity-40" />
        }
        {(e.photo_count > 0 || e.video_count > 0) && (
          <div className="absolute bottom-2 right-2 flex gap-1">
            {e.photo_count > 0 && <span className="bg-black/60 text-white text-[14px] px-2 py-[2px] rounded-full flex items-center gap-1"><ImageIcon size={10} />{e.photo_count}</span>}
            {e.video_count > 0 && <span className="bg-black/60 text-white text-[14px] px-2 py-[2px] rounded-full flex items-center gap-1"><Film size={10} />{e.video_count}</span>}
          </div>
        )}
        {e.event_type && (
          <div className="absolute top-2 left-2">
            <Bg c={K.p} sm>{e.event_type}</Bg>
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="text-[15px] font-bold text-t0 truncate">{e.title}</div>
        <div className="text-[14px] text-t3 mt-1 flex items-center gap-1">
          <Calendar size={10} />{fmtDate(e.event_date)}
          <span className="mx-1">·</span>
          <MapPin size={10} />{e.office_city || e.office_name || e.company_name || '—'}
        </div>
        {e.description && <div className="text-[14px] text-t2 mt-1 line-clamp-2">{e.description}</div>}
      </div>
    </Cd>
  );
};

const UploadEventModal = ({ companies, offices, myProfile, onClose, onCreated }) => {
  const [form, setForm] = useState({
    title: '', description: '', event_date: new Date().toISOString().slice(0, 10),
    event_type: 'Town Hall',
    company_id: myProfile?.employment?.company_id ? String(myProfile.employment.company_id) : (companies[0]?.id ? String(companies[0].id) : ''),
    office_id:  myProfile?.employment?.office_id  ? String(myProfile.employment.office_id)  : '',
  });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setErr('');
    if (!form.title.trim()) return setErr('Event title is required');
    if (!form.event_date) return setErr('Event date is required');
    if (!form.company_id) return setErr('Company is required');
    setBusy(true);
    try {
      const created = await api.post('/api/events', form);
      if (files.length && created?.id) {
        const fd = new FormData();
        files.forEach(f => fd.append('files', f));
        await api.upload('/api/events/' + created.id + '/media', fd);
      }
      onCreated();
    } catch (e) { setErr(e.message || 'Save failed'); }
    finally { setBusy(false); }
  };

  const inputCls = "w-full px-3 py-2 bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none";

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl w-full max-w-[1047px] max-h-[90vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-b0 sticky top-0 bg-bg1 z-10">
          <div className="text-[16px] font-extrabold text-t0">Add Event</div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18} /></button>
        </div>
        <div className="p-5">
          {err && <div className="px-3 py-2 mb-3 rounded-lg text-[14px] font-semibold bg-rb text-r border border-rd">{err}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div className="sm:col-span-2">
              <div className="text-[14px] text-t3 mb-1">Event Title <span className="text-r">*</span></div>
              <input className={inputCls} value={form.title} onChange={e => upd('title', e.target.value)} placeholder="Q3 Town Hall — Bengaluru" />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Event Date <span className="text-r">*</span></div>
              <input type="date" className={inputCls} value={form.event_date} onChange={e => upd('event_date', e.target.value)} />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Event Type</div>
              <select className={inputCls} value={form.event_type} onChange={e => upd('event_type', e.target.value)}>
                {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Company <span className="text-r">*</span></div>
              <select className={inputCls} value={form.company_id} onChange={e => upd('company_id', e.target.value)}>
                <option value="">Select company</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Location / Centre</div>
              <select className={inputCls} value={form.office_id} onChange={e => upd('office_id', e.target.value)}>
                <option value="">— None —</option>
                {offices.map(o => <option key={o.id} value={o.id}>{(o.name || o.nm) + (o.city ? ` — ${o.city}` : '')}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <div className="text-[14px] text-t3 mb-1">Event Description</div>
              <textarea className={inputCls + " min-h-[70px]"} value={form.description} onChange={e => upd('description', e.target.value)} placeholder="A few lines about what this event was about." />
            </div>
            <div className="sm:col-span-2">
              <div className="text-[14px] text-t3 mb-1">Photos &amp; Videos</div>
              <label className="flex items-center justify-center gap-2 px-4 py-6 bg-bg2 border border-dashed border-b1 rounded-xl cursor-pointer hover:border-p">
                <Upload size={14} className="text-t2" />
                <span className="text-[14px] text-t1">
                  {files.length ? `${files.length} file${files.length === 1 ? '' : 's'} selected` : 'Choose photos or videos (multi-select)'}
                </span>
                <input type="file" accept="image/*,video/*" multiple className="hidden"
                  onChange={e => setFiles([...(e.target.files || [])])} />
              </label>
              {files.length > 0 && (
                <div className="text-[12px] text-t3 mt-2">
                  {files.map(f => f.name).join(' · ')}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Bt v="success" onClick={submit} disabled={busy} icon={Check}>{busy ? 'Uploading…' : 'Save Event'}</Bt>
            <Bt v="secondary" onClick={onClose} disabled={busy}>Cancel</Bt>
          </div>
        </div>
      </div>
    </div>
  );
};

const EventDetailDrawer = ({ id, canWrite, canDelete, companies = [], offices = [], onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [addingMedia, setAddingMedia] = useState(false);
  const [newFiles, setNewFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get('/api/events/' + id).then(d => setData(d)).catch(() => setData(null));
  };
  useEffect(load, [id]);

  const openEdit = () => {
    if (!data?.event) return;
    const e = data.event;
    setEditForm({
      title: e.title || '', description: e.description || '',
      event_date: e.event_date ? String(e.event_date).slice(0, 10) : '',
      event_type: e.event_type || 'Town Hall',
      company_id: e.company_id ? String(e.company_id) : '',
      office_id:  e.office_id  ? String(e.office_id)  : '',
    });
    setEditMode(true);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put('/api/events/' + id, {
        ...editForm,
        company_id: editForm.company_id ? parseInt(editForm.company_id) : null,
        office_id:  editForm.office_id  ? parseInt(editForm.office_id)  : null,
      });
      setEditMode(false); load(); onChanged?.();
    } catch (e) { notify(e.message || 'Save failed'); }
    finally { setBusy(false); }
  };

  const uploadMore = async () => {
    if (!newFiles.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      newFiles.forEach(f => fd.append('files', f));
      await api.upload('/api/events/' + id + '/media', fd);
      setAddingMedia(false); setNewFiles([]); load(); onChanged?.();
    } catch (e) { notify(e.message || 'Upload failed'); }
    finally { setBusy(false); }
  };

  const toggleSel = (mid) => {
    setSel(prev => {
      const n = new Set(prev);
      if (n.has(mid)) n.delete(mid); else n.add(mid);
      return n;
    });
  };

  const downloadOne = (m) => {
    const tok = localStorage.getItem('hrms_token');
    // The static /uploads route doesn't take a token — build a fetch-blob
    // download so we stay auth'd against the API for anything protected.
    window.open(`${BASE}/${m.file_path}`, '_blank');
  };

  const downloadZip = (selected) => {
    const tok = localStorage.getItem('hrms_token');
    const q = selected && selected.size ? '?media_ids=' + [...selected].join(',') : '';
    const url = `${BASE}/api/events/${id}/download${q}`;
    // The zip endpoint needs Authorization header — fetch + blob it.
    (async () => {
      try {
        const r = await fetch(url, { headers: { Authorization: 'Bearer ' + tok } });
        if (!r.ok) throw new Error('Download failed');
        const blob = await r.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = (data?.event?.title || 'event').replace(/[^a-zA-Z0-9]+/g, '-') + '.zip';
        document.body.appendChild(a); a.click(); a.remove();
      } catch (e) { notify(e.message || 'Download failed'); }
    })();
  };

  const removeMedia = async (mid) => {
    if (!(await confirmDialog('Remove this photo/video?'))) return;
    try { await api.del(`/api/events/${id}/media/${mid}`); load(); onChanged?.(); }
    catch (e) { notify(e.message || 'Delete failed'); }
  };

  const removeEvent = async () => {
    if (!(await confirmDialog('Delete this event and every photo/video attached? This cannot be undone.'))) return;
    try { await api.del(`/api/events/${id}`); onClose(); onChanged?.(); }
    catch (e) { notify(e.message || 'Delete failed'); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl w-full max-w-[1300px] max-h-[92vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-b0 sticky top-0 bg-bg1 z-10">
          <div className="min-w-0">
            <div className="text-[16px] font-extrabold text-t0 truncate">{data?.event?.title || 'Event'}</div>
            {data?.event && (
              <div className="text-[14px] text-t3 flex items-center gap-2 flex-wrap mt-1">
                {data.event.event_type && <Bg c={K.p} sm>{data.event.event_type}</Bg>}
                <span>{fmtDate(data.event.event_date)}</span>
                <span>·</span>
                <span>{data.event.office_city || data.event.office_name || '—'}</span>
                <span>·</span>
                <span>{data.event.company_name || '—'}</span>
                {data.event.uploaded_by_name && <span className="ml-2">Uploaded by {data.event.uploaded_by_name}</span>}
              </div>
            )}
          </div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1 flex-shrink-0"><X size={18} /></button>
        </div>
        <div className="p-5">
          {!data ? (
            <div className="text-[14px] text-t3">Loading…</div>
          ) : (
            <>
              {data.event.description && (
                <div className="text-[14px] text-t2 mb-4 p-3 rounded-lg bg-bg2 border border-b0">{data.event.description}</div>
              )}
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div className="text-[14px] text-t2">
                  <b className="text-t0">{data.media.length}</b> item{data.media.length === 1 ? '' : 's'}
                  {sel.size > 0 && <> · <b className="text-t0">{sel.size}</b> selected</>}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {canWrite && !editMode && !addingMedia && <Bt v="secondary" s="sm" icon={Edit} onClick={openEdit}>Edit</Bt>}
                  {canWrite && !editMode && !addingMedia && <Bt v="secondary" s="sm" icon={Plus} onClick={() => setAddingMedia(true)}>Add More Media</Bt>}
                  {sel.size > 0 && <Bt v="success" s="sm" icon={Download} onClick={() => downloadZip(sel)}>Download Selected ({sel.size})</Bt>}
                  {data.media.length > 0 && <Bt s="sm" icon={Download} onClick={() => downloadZip()}>Download Album</Bt>}
                  {canDelete && <Bt v="danger" s="sm" icon={Trash2} onClick={removeEvent}>Delete Event</Bt>}
                </div>
              </div>

              {editMode && (
                <div className="mb-4 p-3 rounded-lg bg-bg2 border border-b0">
                  <div className="text-[14px] font-semibold text-t0 mb-2 flex items-center gap-1"><Edit size={12} color={K.p} />Edit event</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="sm:col-span-2">
                      <div className="text-[14px] text-t3 mb-1">Title</div>
                      <input className="w-full px-3 py-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] text-t0 outline-none" value={editForm.title || ''} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} />
                    </div>
                    <div>
                      <div className="text-[14px] text-t3 mb-1">Event Date</div>
                      <input type="date" className="w-full px-3 py-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] text-t0 outline-none" value={editForm.event_date || ''} onChange={e => setEditForm(f => ({ ...f, event_date: e.target.value }))} />
                    </div>
                    <div>
                      <div className="text-[14px] text-t3 mb-1">Event Type</div>
                      <select className="w-full px-3 py-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] text-t0 outline-none" value={editForm.event_type || ''} onChange={e => setEditForm(f => ({ ...f, event_type: e.target.value }))}>
                        {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <div className="text-[14px] text-t3 mb-1">Company</div>
                      <select className="w-full px-3 py-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] text-t0 outline-none" value={editForm.company_id || ''} onChange={e => setEditForm(f => ({ ...f, company_id: e.target.value }))}>
                        <option value="">Select</option>
                        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <div className="text-[14px] text-t3 mb-1">Location / Centre</div>
                      <select className="w-full px-3 py-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] text-t0 outline-none" value={editForm.office_id || ''} onChange={e => setEditForm(f => ({ ...f, office_id: e.target.value }))}>
                        <option value="">— None —</option>
                        {offices.map(o => <option key={o.id} value={o.id}>{(o.name || o.nm) + (o.city ? ` — ${o.city}` : '')}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <div className="text-[14px] text-t3 mb-1">Description</div>
                      <textarea className="w-full min-h-[60px] p-2 bg-bg1 border border-b0 rounded-[8px] text-[14px] outline-none" value={editForm.description || ''} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Bt v="success" s="sm" icon={Check} onClick={saveEdit} disabled={busy}>Save Changes</Bt>
                    <Bt v="secondary" s="sm" onClick={() => setEditMode(false)} disabled={busy}>Cancel</Bt>
                  </div>
                </div>
              )}

              {addingMedia && (
                <div className="mb-4 p-3 rounded-lg bg-bg2 border border-b0">
                  <div className="text-[14px] font-semibold text-t0 mb-2 flex items-center gap-1"><Plus size={12} color={K.p} />Add more photos / videos</div>
                  <label className="flex items-center justify-center gap-2 px-4 py-6 bg-bg1 border border-dashed border-b1 rounded-xl cursor-pointer hover:border-p">
                    <Upload size={14} className="text-t2" />
                    <span className="text-[14px] text-t1">{newFiles.length ? `${newFiles.length} file${newFiles.length === 1 ? '' : 's'} selected` : 'Choose files'}</span>
                    <input type="file" accept="image/*,video/*" multiple className="hidden"
                      onChange={e => setNewFiles([...(e.target.files || [])])} />
                  </label>
                  {newFiles.length > 0 && <div className="text-[12px] text-t3 mt-2">{newFiles.map(f => f.name).join(' · ')}</div>}
                  <div className="flex gap-2 mt-2">
                    <Bt v="success" s="sm" icon={Upload} onClick={uploadMore} disabled={busy || !newFiles.length}>Upload {newFiles.length || ''}</Bt>
                    <Bt v="secondary" s="sm" onClick={() => { setAddingMedia(false); setNewFiles([]); }} disabled={busy}>Cancel</Bt>
                  </div>
                </div>
              )}
              {data.media.length === 0 ? (
                <div className="text-center py-10 text-t3 text-[14px]">No photos or videos on this event yet.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {data.media.map(m => {
                    const on = sel.has(m.id);
                    const src = BASE + '/' + m.file_path;
                    return (
                      <div key={m.id} className="relative group rounded-lg overflow-hidden border border-b0 bg-bg2">
                        <div className="aspect-square flex items-center justify-center">
                          {m.kind === 'photo'
                            ? <img src={src} alt={m.file_name} className="w-full h-full object-cover" />
                            : <video src={src} controls preload="metadata" className="w-full h-full object-cover" />
                          }
                        </div>
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => toggleSel(m.id)}
                            className={`w-6 h-6 rounded border cursor-pointer flex items-center justify-center text-[14px] font-bold ${on ? 'bg-g text-white border-g' : 'bg-white/90 text-t2 border-white/60'}`}>
                            {on ? '✓' : ''}
                          </button>
                          <div className="flex gap-1">
                            <button onClick={() => downloadOne(m)} title="Download"
                              className="w-6 h-6 rounded bg-white/90 border-0 text-t1 cursor-pointer flex items-center justify-center"><Download size={11} /></button>
                            {canDelete && <button onClick={() => removeMedia(m.id)} title="Remove"
                              className="w-6 h-6 rounded bg-white/90 border-0 text-r cursor-pointer flex items-center justify-center"><Trash2 size={11} /></button>}
                          </div>
                        </div>
                        {m.kind === 'video' && (
                          <div className="absolute top-1 left-1"><Bg c={K.pu} sm><Film size={9} className="inline mr-1" />Video</Bg></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
